import bearing from "@turf/bearing";
import { toLonLat } from "ol/proj";
import { LineString, Point } from "ol/geom.js";
import { Fill, Stroke, Style, Text } from "ol/style.js";

/**
 * Converts map measurement to selected unit measurement
 * @param {number} area polygon.getArea() output
 * @param {string} areaUnit Selected unit of measurement
 * @returns {string} Converted measurement
 */
export const calculateAreaFromFeet = (area, areaUnit) => {
    let output;
    if (areaUnit === "Feet \xB2") {
        output = (+area.toFixed(2)).toLocaleString("en-US") + " ft\xB2";
    } else if (areaUnit === "Yards \xB2") {
        output = (+(area / 9).toFixed(2)).toLocaleString("en-US") + " yds\xB2";
    } else if (areaUnit === "Meters \xB2") {
        output = (+(area / 10.7639).toFixed(2)).toLocaleString("en-US") + " m\xB2";
    } else if (areaUnit === "Kilometers \xB2") {
        output =
            (+(area / 10.7639 / 1000000).toFixed(2)).toLocaleString("en-US") + " km\xB2";
    } else if (areaUnit === "Miles \xB2") {
        output = (+(area / 27878400).toFixed(2)).toLocaleString("en-US") + " mi\xB2";
    } else if (areaUnit === "Nautical Miles \xB2") {
        output =
            (+(area / 36919234.2544).toFixed(2)).toLocaleString("en-US") + " nmi\xB2";
    } else if (areaUnit === "Acres") {
        output = (+(area / 43560).toFixed(2)).toLocaleString("en-US") + " ac";
    } else if (areaUnit === "Hectares") {
        output = (+(area / 11577760000).toFixed(2)).toLocaleString("en-US") + " ha";
    }
    return output;
};

/**
 * Converts map measurement in feet to selected unit measurement
 * @param {number} area linestring.getLength() output
 * @param {string} areaUnit Selected unit of measurement
 * @returns {string} Converted measurement
 */
export const calculateLengthFromFeet = (length, distanceUnit) => {
    let output;
    if (distanceUnit === "Feet") {
        output = (+length.toFixed(2)).toLocaleString("en-US") + " ft";
    } else if (distanceUnit === "Yards") {
        output = (+(length / 3).toFixed(2)).toLocaleString("en-US") + " yds";
    } else if (distanceUnit === "Meters") {
        output = (+(length / 3.28084).toFixed(2)).toLocaleString("en-US") + " m";
    } else if (distanceUnit === "Kilometers") {
        output = (+(length / 3.28084 / 1000).toFixed(2)).toLocaleString("en-US") + " km";
    } else if (distanceUnit === "Miles") {
        output = (+(length / 5280).toFixed(2)).toLocaleString("en-US") + " mi";
    } else if (distanceUnit === "Nautical Miles") {
        output = (+(length / 6076.12).toFixed(2)).toLocaleString("en-US") + " nmi";
    }
    return output;
};

/**
 * Converts map measurement in meters to selected unit measurement
 * @param {number} area polygon.getArea() output
 * @param {string} areaUnit Selected unit of measurement
 * @returns {string} Converted measurement
 */
export const calculateAreaFromMeters = (area, areaUnit) => {
    let output;
    if (areaUnit === "Feet \xB2") {
        output = (+(area / 10.7639)).toLocaleString("en-US") + " ft\xB2";
    } else if (areaUnit === "Yards \xB2") {
        output = (+(area / 1.19599).toFixed(2)).toLocaleString("en-US") + " yds\xB2";
    } else if (areaUnit === "Meters \xB2") {
        output = (+area.toFixed(2)).toLocaleString("en-US") + " m\xB2";
    } else if (areaUnit === "Kilometers \xB2") {
        output = (+(area / 1000000).toFixed(2)).toLocaleString("en-US") + " km\xB2";
    } else if (areaUnit === "Miles \xB2") {
        output =
            (+(area / 3.861 / 10000000).toFixed(2)).toLocaleString("en-US") + " mi\xB2";
    } else if (areaUnit === "Nautical Miles \xB2") {
        output =
            (+(area / 2.91553 / 10000000).toFixed(2)).toLocaleString("en-US") +
            " nmi\xB2";
    } else if (areaUnit === "Acres") {
        output = (+(area / 4046.68).toFixed(2)).toLocaleString("en-US") + " ac";
    } else if (areaUnit === "Hectares") {
        output = (+(area / 10000).toFixed(2)).toLocaleString("en-US") + " ha";
    }
    return output;
};

/**
 * Converts map measurement in meters to selected unit measurement
 * @param {number} area linestring.getLength() output
 * @param {string} areaUnit Selected unit of measurement
 * @returns {string} Converted measurement
 */
export const calculateLengthFromMeters = (length, distanceUnit) => {
    let output;
    if (distanceUnit === "Feet") {
        output = (+(length * 3.28084).toFixed(2)).toLocaleString("en-US") + " ft";
    } else if (distanceUnit === "Yards") {
        output = (+(length * 1.09361).toFixed(2)).toLocaleString("en-US") + " yds";
    } else if (distanceUnit === "Meters") {
        output = (+length.toFixed(2)).toLocaleString("en-US") + " m";
    } else if (distanceUnit === "Kilometers") {
        output = (+(length / 1000).toFixed(2)).toLocaleString("en-US") + " km";
    } else if (distanceUnit === "Miles") {
        output = (+(length / 1609.34).toFixed(2)).toLocaleString("en-US") + " mi";
    } else if (distanceUnit === "Nautical Miles") {
        output = (+(length / 1852).toFixed(2)).toLocaleString("en-US") + " nmi";
    }
    return output;
};

/**
 * Takes a line, calculates bearing and outputs a label
 * @param {number[]} pointsArr Array of 2 points on map
 * @returns {string} Bearing label
 */
export const calculateBearing = (pointsArr) => {
    const point1 = toLonLat(pointsArr[1]);
    const point2 = toLonLat(pointsArr[0]);
    const bearingCalc = bearing(point1, point2);
    let coordDMS;
    if (bearingCalc > 0 && bearingCalc <= 90) {
        coordDMS = `S ${toDMS(bearingCalc)} W`;
    } else if (bearingCalc > 90 && bearingCalc <= 180) {
        coordDMS = (180 - bearingCalc).toFixed(3);
        coordDMS = `N ${toDMS(coordDMS)} W`;
    } else if (bearingCalc < 0 && bearingCalc >= -90) {
        coordDMS = (bearingCalc * -1).toFixed(3);
        coordDMS = `S ${toDMS(coordDMS)} E`;
    } else if (bearingCalc < -90 && bearingCalc >= -180) {
        coordDMS = (bearingCalc + 180).toFixed(3);
        coordDMS = `N ${toDMS(coordDMS)} E`;
    }
    return coordDMS;
};

/**
 * Converts degrees in decimals to degrees in DMS
 * @param {number} inNum Represents a degree
 * @returns {string} Formatted as DMS
 */
const toDMS = (inNum) => {
    const degrees = Math.floor(inNum);
    const minutes = Math.floor((inNum - degrees) * 60);
    const seconds = Math.floor(((inNum - degrees) * 60 - minutes) * 60);
    return `${degrees}° ${minutes}′ ${seconds}″`;
};

const meters = ["meters", "metres", "metre", "meter"];

/**
 * @typedef MeasureUnitsState
 * @property {string} configUnits Map units from configuration file ex: "US_Survey_Feet"
 * @property {string} areaUnits Current units to measure area with
 * @property {string} distanceUnits Current units to measure distance with
 * @property {boolean} bearing Whether or not to show bearing measurement
 */

/**
 * Takes openlayers geometry and outputs measurement label
 * @param {MeasureUnitsState} unitArgs
 * @param {object} geometry Openlayers LineString geometry
 * @returns {string} Label for line measurement on map
 */
export const formatLength = (unitArgs, geometry) => {
    const { configUnits, distanceUnits } = unitArgs;
    const length = geometry.getLength();
    const output = meters.includes(configUnits.toLowerCase())
        ? calculateLengthFromMeters(length, distanceUnits)
        : calculateLengthFromFeet(length, distanceUnits);
    return output;
};

/**
 * Takes openlayers geometry and outputs measurement label
 * @param {MeasureUnitsState} unitArgs
 * @param {object} geometry Openlayers Polygon geometry
 * @returns {string} Label for polygon measurement on map
 */
export const formatArea = (unitArgs, geometry) => {
    const { configUnits, areaUnits } = unitArgs;
    const area = geometry.getArea();
    const output = meters.includes(configUnits.toLowerCase())
        ? calculateAreaFromMeters(area, areaUnits)
        : calculateAreaFromFeet(area, areaUnits);
    return output;
};

/**
 * Takes openlayers geometry and outputs measurement label
 * @param {MeasureUnitsState} unitArgs
 * @param {number} radiusLength Length of radius in map units
 * @returns {string} Label for circle measurement on map
 */
export const formatCircleArea = (unitArgs, radiusLength) => {
    const { configUnits, areaUnits } = unitArgs;
    const area = radiusLength * radiusLength * Math.PI;
    const output = meters.includes(configUnits.toLowerCase())
        ? calculateAreaFromMeters(area, areaUnits)
        : calculateAreaFromFeet(area, areaUnits);
    return output;
};

const segmentStyle = new Style({
    zIndex: 1000,
    text: new Text({
        font: "12px Tahoma",
        fill: new Fill({
            color: "white",
        }),
        stroke: new Stroke({ color: "black", width: 4 }),
        placement: "line",
        maxAngle: Math.PI * 2,
        padding: [2, 2, 2, 2],
        textBaseline: "bottom",
        offsetY: -2,
    }),
});

const bearingStyle = new Style({
    zIndex: 1000,
    text: new Text({
        font: "11px Tahoma",
        fill: new Fill({
            color: "white",
        }),
        stroke: new Stroke({ color: "black", width: 4 }),
        placement: "line",
        maxAngle: Math.PI * 2,
        padding: [-5, 2, -5, 2],
        textBaseline: "top",
        offsetY: 5,
    }),
});

const segmentStyles = [segmentStyle];
const bearingStyles = [bearingStyle];
/**
 * Function for drawing measurement labels on map
 * @param {Object} feature Feature from map
 * @param {Object} style OpenLayers style object
 * @param {MeasureUnitsState} unitArgs User selected units and parameters
 * @returns OpenLayers style object
 */
export const drawStyleFunction = (feature, style, unitArgs) => {
    const styles = [style];
    const geometry = feature.getGeometry();
    const type = geometry.getType();
    let point, label, line, radius;
    if (type === "Polygon") {
        point = geometry.getInteriorPoint();
        label = formatArea(unitArgs, geometry);
        line = new LineString(geometry.getCoordinates()[0]);
    } else if (type === "LineString") {
        point = new Point(geometry.getLastCoordinate());
        label = formatLength(unitArgs, geometry);
        line = geometry;
    } else if (type === "Circle") {
        const center = geometry.getCenter();
        const radiusLength = geometry.getRadius();
        const area = formatCircleArea(unitArgs, radiusLength);
        point = new Point(center);
        label = area;
        radius = new LineString([[...center], [center[0] + radiusLength, center[1]]]);
        radiusStyle.setGeometry(radius);
        radiusStyle.getText().setText(formatLength(unitArgs, radius));
        styles.push(radiusStyle);
    }
    if (line) {
        let count = 0;
        line.forEachSegment((a, b) => {
            const segment = new LineString([a, b]);
            const label = formatLength(unitArgs, segment);
            if (segmentStyles.length - 1 < count) {
                segmentStyles.push(segmentStyle.clone());
            }
            segmentStyles[count].setGeometry(segment);
            segmentStyles[count].getText().setText(label);
            styles.push(segmentStyles[count]);
            if (unitArgs.bearing) {
                const bearingLabel = calculateBearing([a, b]);
                if (bearingStyles.length - 1 < count) {
                    bearingStyles.push(bearingStyle.clone());
                }
                bearingStyles[count].setGeometry(segment);
                bearingStyles[count].getText().setText(bearingLabel);
                styles.push(bearingStyles[count]);
            }
            count++;
        });
    }
    if (label) {
        if (type === "Polygon") {
            polygonLabel.setGeometry(point);
            polygonLabel.getText().setText(label);
            styles.push(polygonLabel);
        } else if (type === "LineString") {
            lineLabel.setGeometry(point);
            lineLabel.getText().setText(label);
            styles.push(lineLabel);
        } else if (type === "Circle") {
            circleLabel.setGeometry(point);
            circleLabel.getText().setText(label);
            styles.push(circleLabel);
        }
    }
    return styles;
};

const radiusStyle = new Style({
    zIndex: 1000,
    text: new Text({
        font: "12px Tahoma",
        fill: new Fill({
            color: "white",
        }),
        stroke: new Stroke({ color: "black", width: 4 }),
        placement: "line",
        offsetY: -2,
    }),
    stroke: new Stroke({
        color: "rgba(0, 0, 255, 0.7)",
        width: 2,
    }),
});

const circleLabel = new Style({
    zIndex: 1000,
    text: new Text({
        font: "bold 14px Tahoma",
        fill: new Fill({
            color: "white",
        }),
        stroke: new Stroke({ color: "black", width: 3 }),
        overflow: true,
        placement: "point",
        offsetY: 15,
        offsetX: 8,
    }),
});

const polygonLabel = new Style({
    zIndex: 1000,
    text: new Text({
        font: "bold 14px Tahoma",
        fill: new Fill({
            color: "white",
        }),
        stroke: new Stroke({ color: "black", width: 3 }),
        // backgroundFill: new Fill({
        //   color: "rgba(0, 0, 0, 0.4)",
        // }),
        overflow: true,
        placement: "point",
    }),
});

const lineLabel = new Style({
    zIndex: 1000,
    text: new Text({
        font: "bold 14px Tahoma",
        fill: new Fill({
            color: "white",
        }),
        stroke: new Stroke({ color: "black", width: 3 }),
        overflow: true,
        placement: "point",
        textAlign: "left",
        offsetY: 15,
        offsetX: 8,
    }),
});
